import { Injectable } from "@angular/core";
import { DetailRequest, Filter, FilterBuilder, FilterQueryMode, SampleService, Status, translateCommonErrorStatus } from "@core/data";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs/operators";
import {
    ClearSamplePickerFilter,
    InitSamplePicker,
    LoadSamplePicker,
    PageSamplePicker,
    QuerySamplePicker,
    SortSamplePicker,
} from "./image-move-sample-picker.state-actions";
import { DEFAULTS, ImageMoveSamplePickerStateModel } from "./image-move-sample-picker.state-model";

@State({
    name: "image_move_sample_picker",
    defaults: DEFAULTS
})
@Injectable()
export class ImageMoveSamplePickerState {

    @Selector()
    static query(state: ImageMoveSamplePickerStateModel) {
        return state.query;
    }

    constructor(private _sampleService: SampleService) {}

    @Action(InitSamplePicker)
    initSamplePicker(ctx: StateContext<ImageMoveSamplePickerStateModel>, action: InitSamplePicker) {

        ctx.setState({
            ...DEFAULTS,
            orgKey: action.orgKey,
            sampleType: action.sampleType,
            excludeSampleKey: action.excludeSampleKey,
        });

        return ctx.dispatch(new LoadSamplePicker);
    }

    @Action(LoadSamplePicker, { cancelUncompleted: true })
    loadSamplePicker(ctx: StateContext<ImageMoveSamplePickerStateModel>) {
        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING
        });

        const detail: DetailRequest = {
            related: [
                'scionCultivar',
                'primaryImage',
                'site',
            ],
        };

        return this._sampleService
            .query(this.getCurrentFilter(state), detail)
            .pipe(
                tap(
                    result => {
                        if (result.total > 0) {
                            ctx.patchState({
                                status: Status.OK,
                                data: result.data,
                                total: result.total
                            });
                        } else {
                            ctx.patchState({
                                status: Status.EMPTY,
                                data: [],
                                total: 0
                            });
                        }
                    },
                    err => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(err),
                            data: [],
                            total: 0
                        });
                    }
                )
            );
    }

    @Action(QuerySamplePicker)
    querySamplePicker(ctx: StateContext<ImageMoveSamplePickerStateModel>, action: QuerySamplePicker) {

        const state = ctx.getState();

        ctx.patchState({
            query: { ...state.query, ...action.query },
            page: {
                index: 0,
                size: state.page.size
            }
        });

        return ctx.dispatch(new LoadSamplePicker);
    }

    @Action(PageSamplePicker)
    pageSamplePicker(ctx: StateContext<ImageMoveSamplePickerStateModel>, action: PageSamplePicker) {
        ctx.patchState({
            page: {
                index: action.index,
                size: action.size
            }
        });

        return ctx.dispatch(new LoadSamplePicker);
    }

    @Action(SortSamplePicker)
    sortSamplePicker(ctx: StateContext<ImageMoveSamplePickerStateModel>, action: SortSamplePicker) {

        const state = ctx.getState();

        ctx.patchState({
            sort: {
                column: action.column,
                order: action.order
            },
            page: {
                index: 0,
                size: state.page.size
            }
        });

        return ctx.dispatch(new LoadSamplePicker);
    }

    @Action(ClearSamplePickerFilter)
    clearSamplePickerFilter(ctx: StateContext<ImageMoveSamplePickerStateModel>) {

        ctx.patchState({
            query: DEFAULTS.query,
            page: DEFAULTS.page,
            sort: DEFAULTS.sort
        });

        return ctx.dispatch(new LoadSamplePicker);
    }

    getCurrentFilter(state: ImageMoveSamplePickerStateModel): Filter {

        const fb = new FilterBuilder();

        fb.setPage(state.page);
        fb.setSort(state.sort);

        fb.setQuery('ownerOrgKey', state.orgKey);

        fb.setQuery('type', state.sampleType);
        fb.setQuery('sampleKey', state.excludeSampleKey, FilterQueryMode.NOT_EQUALS);

        if (Array.isArray(state.query.scionCultivars) && state.query.scionCultivars.length > 0) {
            let keys = state.query.scionCultivars.map(i => i.key);
            fb.setQuery('scionCultivarKey', keys, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.sites) && state.query.sites.length > 0) {
            let keys = state.query.sites.map(i => i.key);
            fb.setQuery('siteKey', keys, FilterQueryMode.IN);
        }

        if (Array.isArray(state.query.scionCropId) && state.query.scionCropId.length > 0) {
            fb.setQuery('scionCropId', state.query.scionCropId, FilterQueryMode.IN);
        }

        if (state.query.hideDestroyed) {
            fb.setQuery('deathDate', new Date(), FilterQueryMode.HAS_NO_VALUE);
        }

        if (state.query.startEvalPeriod && state.query.endEvalPeriod) {
            fb.setQuery('evalPeriod', [state.query.startEvalPeriod, state.query.endEvalPeriod], FilterQueryMode.BETWEEN);
        }

        if (state.query.search) {
            if (state.query.search.trim()) fb.setQuery('$fuzzy', state.query.search);
        }

        return fb.get();
    }

}
